import { CalculationMethod, IPitchState, PlatoGravity, WortUnits } from './types';

/**
 * wort volume converstion matrix
 * liters to X unit
 */
export const volumeToLiterRatio = {
  [WortUnits.Liter]: 1,
  [WortUnits.Kilogram]: 1,
  [WortUnits.Gram]: 1000,
  [WortUnits.Ounce]: 35.274,
  [WortUnits.Mililiter]: 1000,
  [WortUnits.Barrels]: 0.00852,
  [WortUnits.Hectorliter]: 0.01,
  [WortUnits.Pound]: 2.20462262,
  [WortUnits.Gallon]: 0.264172052,
};

export type YeastVolumeMatrix = { [key in WortUnits]?: number };

interface IWortConvert {
  wort: number;
  wortUnit: WortUnits;
}

interface IGravityConvert {
  gravity: number;
  gravityType: PlatoGravity;
}

interface ICalculate {
  wort: number;
  viability: number;
  wortUnit: WortUnits;
  concentration: number;
  targetConcentration: number;
  calculationMethod: CalculationMethod;
}

export const getWortVolumeInLiter = ({ wort, wortUnit }: IWortConvert) => {
  if (!volumeToLiterRatio[wortUnit]) throw Error(`Unkown wort unit type ${wortUnit}`);
  return wort / volumeToLiterRatio[wortUnit];
};

export const getWortLiterVolumeInUnit = ({ wort, wortUnit }: IWortConvert) => {
  if (!volumeToLiterRatio[wortUnit]) throw Error(`Unkown wort unit type ${wortUnit}`);
  return wort * volumeToLiterRatio[wortUnit];
};

export const calculateYeastVolume = ({
  wort,
  wortUnit,
  viability = 100,
  concentration,
  calculationMethod,
  targetConcentration: tc,
}: ICalculate) => {
  const wortVolume = getWortVolumeInLiter({ wort, wortUnit });
  let yeastVolume = 0;
  if (calculationMethod === CalculationMethod.IncludingYeastVolume)
    yeastVolume = ((tc * wortVolume) / (concentration - tc) / viability) * 100;
  if (calculationMethod === CalculationMethod.ExcludingYeastVolume)
    yeastVolume = ((tc * wortVolume) / concentration / viability) * 100;
  return isNaN(yeastVolume) ? 0 : yeastVolume;
};

export const calculateYeastVolumeForAllUnits = (
  state: Partial<ICalculate>,
): YeastVolumeMatrix => {
  // @ts-expect-error
  const yeastVolume = calculateYeastVolume(state);
  const convertedYeastVolume: any = {};
  for (const [unit, ratio] of Object.entries(volumeToLiterRatio)) {
    convertedYeastVolume[unit] = yeastVolume * ratio;
  }
  return convertedYeastVolume;
};

const getGravity = ({ gravity, gravityType }: IGravityConvert) => {
  return gravityType === PlatoGravity.Gravity
    ? -1 * 616.868 +
        1111.14 * gravity -
        630.272 * Math.pow(gravity, 2) +
        135.997 * Math.pow(gravity, 3)
    : gravity;
};

export const onGravityChange = ({
  state,
  gravity,
}: {
  state: IPitchState;
  gravity: number;
}) => {
  const { pitchRate, targetConcentration, gravityType } = state;
  const _gravity = getGravity({ gravity, gravityType });

  if (pitchRate) {
    const targetConcentration = _gravity * pitchRate;
    return { ...state, gravity, pitchRate, targetConcentration };
  }

  if (targetConcentration) {
    const pitchRate = targetConcentration / _gravity;
    return { ...state, gravity, pitchRate, targetConcentration };
  }

  return { ...state, gravity };
};

export const onPitchRateChange = ({
  state,
  pitchRate,
}: {
  state: IPitchState;
  pitchRate: number;
}) => {
  const { gravity, gravityType, targetConcentration } = state;

  if (gravity) {
    const _gravity = getGravity({ gravity, gravityType });
    return { ...state, pitchRate, targetConcentration: _gravity * pitchRate };
  }

  if (targetConcentration) {
    const _gravity = targetConcentration / pitchRate;
    return { ...state, pitchRate, targetConcentration, gravity: _gravity };
  }

  return { ...state, pitchRate };
};
